import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EmployeeOperationalTask } from '../../interfaces/EmployeeInterface';
import {
  PayrollEntryResult, PayrollOperationalTaskBody,
} from '../../interfaces/TimesheetEditInterface';
import { formatMinutesAs } from '../../util/hoursFormat';
import { money, parseDur, ymdLabel } from './clockParse';
import { OperationalTaskOption, getOperationalTasksCached } from './lookups';
import { EntryErrorBanner, EntryModalShell, performEntrySave } from './EntryModalShell';
import { DurEchoParts } from './echoParts';

/**
 * Operational task editor (WCDP-only kind, §8.7). Payroll picks the operational
 * task and enters HOURS via the 10-key duration field — NO amount is ever sent:
 * the server computes the stored amount from the employee's system rate × hours.
 * The modal shows a clearly-provisional client estimate from the state read's
 * `hourlyRate`; the day card shows the server's stored amount after save.
 */

export interface OperationalCarry { operationalTaskID: number | null; }

export interface OperationalTaskModalProps {
  employeeID: number;
  payPeriodID: number;
  ymd: string;
  employeeName: string;
  editSessionID: string;
  /** The employee's system hourly rate (state read) — the ESTIMATE only. */
  hourlyRate: number;
  entry: EmployeeOperationalTask | null;
  carry: OperationalCarry | null;
  onSaved: (result: PayrollEntryResult, close: boolean, carry: OperationalCarry) => void;
  onCancel: () => void;
  onConflict: (message: string) => void;
}

const OperationalTaskModal: React.FC<OperationalTaskModalProps> = ({
  employeeID, payPeriodID, ymd, employeeName, editSessionID, hourlyRate,
  entry, carry, onSaved, onCancel, onConflict,
}) => {
  const isEdit = entry != null;
  const [options, setOptions] = useState<OperationalTaskOption[]>([]);
  const [operationalTaskID, setOperationalTaskID] = useState<number | null>(
    isEdit ? entry!.operationalTaskID : carry?.operationalTaskID ?? null
  );
  // Prefill as H:MM — parseDur round-trips it exactly.
  const [minsRaw, setMinsRaw] = useState<string>(
    isEdit ? formatMinutesAs(entry!.minutesClaimed ?? 0, 'hhmm') : ''
  );
  const [desc, setDesc] = useState<string>(isEdit ? entry!.description ?? '' : '');
  const [bad, setBad] = useState<'mins' | null>(null);
  const [banner, setBanner] = useState<{ title: string; body?: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const minsRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getOperationalTasksCached()
      .then((rows) => {
        setOptions(rows);
        setOperationalTaskID((current) => current ?? rows[0]?.operationalTaskID ?? null);
      })
      .catch(() => setOptions([]));
  }, []);

  const selected = useMemo(
    () => options.find((o) => o.operationalTaskID === operationalTaskID) ?? null,
    [options, operationalTaskID]
  );

  const handleSave = async (close: boolean) => {
    if (operationalTaskID == null) {
      setBanner({ title: 'Pick an operational task first.', body: 'The entry must name an operational task.' });
      return;
    }
    const mins = parseDur(minsRaw);
    if (mins == null || mins <= 0) { setBad('mins'); minsRef.current?.focus(); return; }

    // NO amount field — the server computes rate × hours (the contract). The
    // API body takes HOURS; parseDur yields minutes, and minutes/60 round-trips
    // exactly through the server's round(hours*60).
    const body: PayrollOperationalTaskBody = {
      ...(isEdit ? {} : { employeeID, payPeriodID, entryDate: ymd }),
      operationalTaskID,
      hours: mins / 60,
      description: desc,
      editSessionID,
    };

    setSaving(true);
    setBanner(null);
    const outcome = await performEntrySave(
      'operationalTask',
      isEdit ? entry!.employeeOperationalTaskID : null,
      body
    );
    setSaving(false);
    if (!outcome.ok) {
      if (outcome.conflict) onConflict(outcome.message);
      else setBanner({ title: 'Could not save the operational task.', body: outcome.message });
      return;
    }
    onSaved(outcome.result, close, { operationalTaskID });
  };

  const mins = parseDur(minsRaw);
  const estimate = mins != null && mins > 0 && hourlyRate > 0
    ? Math.round(hourlyRate * (mins / 60) * 100) / 100
    : null;

  return (
    <EntryModalShell
      kind="operationalTask"
      mode={isEdit ? 'edit' : 'add'}
      subtitle={`${ymdLabel(ymd)} · ${employeeName}`}
      banner={banner ? <EntryErrorBanner title={banner.title} body={banner.body} /> : null}
      echo={(
        <>
          <DurEchoParts raw={minsRaw} />
          {estimate != null && <span>≈ <b>{money(estimate)}</b> est.</span>}
        </>
      )}
      saving={saving}
      onSave={handleSave}
      onCancel={onCancel}
    >
      <div className="field">
        <label>Operational task</label>
        <select
          className="pe-in sans"
          value={operationalTaskID ?? ''}
          onChange={(ev) => setOperationalTaskID(ev.target.value === '' ? null : Number(ev.target.value))}
        >
          <option value="">{options.length ? 'Select operational task…' : 'Loading operational tasks…'}</option>
          {options.map((o) => (
            <option key={o.operationalTaskID} value={o.operationalTaskID}>
              {o.name}
            </option>
          ))}
        </select>
        {selected?.job ? (
          <p className="pe-note">Job {selected.job}</p>
        ) : null}
      </div>
      <div className="row2">
        <div className="field">
          <label>Hours worked</label>
          <input
            ref={minsRef}
            className={`pe-in${bad === 'mins' ? ' bad' : ''}`}
            value={minsRaw}
            onChange={(ev) => { setMinsRaw(ev.target.value); if (bad === 'mins') setBad(null); }}
            placeholder="8 or 8:00"
            autoFocus
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div className="field">
          <label>Amount (computed on save)</label>
          <input
            className="pe-in"
            value={estimate != null ? `≈ ${money(estimate)}` : '—'}
            readOnly
            tabIndex={-1}
            aria-label="Estimated amount — the server computes the final amount on save"
          />
        </div>
      </div>
      <p className="pe-note">
        {hourlyRate > 0
          ? <>≈ {estimate != null ? money(estimate) : '$0.00'} at {money(hourlyRate)}/hr — an estimate only. The server computes and stores the final amount from the employee&rsquo;s system rate when you save.</>
          : <>The server computes the amount from the employee&rsquo;s system rate × hours when you save — no rate is on file to estimate from here.</>}
      </p>
      <div className="field">
        <label>Description (optional)</label>
        <input
          className="pe-in sans"
          value={desc}
          onChange={(ev) => setDesc(ev.target.value)}
          placeholder="Shop time, yard maintenance"
          autoComplete="off"
        />
      </div>
    </EntryModalShell>
  );
};

export default OperationalTaskModal;
